import {
  Grid,
  makeStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { forwardRef } from 'react';
import { Lambda } from '../../LambdaListEditor';
import { LambdaItemHeader } from '../shared/LambdaItemHeader';
import { LambdaBasicFields } from '../shared/LambdaBasicFields';
import { LambdaNameInputRef } from '../shared/LambdaNameInput';
import { LAMBDA_ORANGE, LAMBDA_ORANGE_LIGHT } from '../shared/constants';
import { LambdaFieldsEditor } from './LambdaFieldsEditor';
import { LambdaIamSection } from './LambdaIamSection';
import { LambdaAdditionalSettingsSection } from './LambdaAdditionalSettingsSection';
import LambdaIcon from '../../../assets/aws/lambda.svg';

const useStyles = makeStyles(theme => ({
  lambdaItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `2px solid ${LAMBDA_ORANGE_LIGHT}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    position: 'relative',
    '&:hover': {
      boxShadow: theme.shadows[2],
      borderColor: LAMBDA_ORANGE,
    },
  },
  advancedSection: {
    marginTop: theme.spacing(2),
    '&:before': {
      display: 'none',
    },
  },
  advancedSummary: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    minHeight: 48,
    '&.Mui-expanded': {
      minHeight: 48,
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
  },
  icon: {
    width: '1.25rem',
    height: '1.25rem',
  },
  advancedDetails: {
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
}));

interface LambdaItemEditorProps {
  name: string;
  lambda: Lambda;
  buildNumber?: boolean;
  onUpdate: (
    name: string,
    field: keyof Lambda,
    value: string | number | boolean | Record<string, string> | string[],
  ) => void;
  onRename: (oldName: string, newName: string) => void;
  onDelete: (name: string) => void;
}

export const LambdaItemEditor = forwardRef<LambdaNameInputRef, LambdaItemEditorProps>(
  ({ name, lambda, buildNumber = true, onUpdate, onRename, onDelete }, ref) => {
    const classes = useStyles();

    const handleLambdaFieldsChange = (updates: Partial<Lambda>) => {
      Object.keys(updates).forEach(key => {
        const field = key as keyof Lambda;
        onUpdate(name, field, updates[field] as any);
      });
    };

    return (
      <div className={classes.lambdaItem}>
        <LambdaItemHeader ref={ref} name={name} onRename={onRename} onDelete={onDelete} />
        <Grid container spacing={2}>
          <LambdaBasicFields
            lambda={lambda}
            onUpdate={(field, value) => onUpdate(name, field as keyof Lambda, value)}
            showBuildNumber={buildNumber}
          />
        </Grid>

        {/* Lambda Configuration Section */}
        <Accordion className={classes.advancedSection} defaultExpanded={false}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.advancedSummary}
            data-ai-anchor={`lambda.${name}.settings`}
          >
            <div className={classes.titleContainer}>
              <img src={LambdaIcon} alt="Lambda" className={classes.icon} />
              <Typography variant="subtitle2">Runtime & Environment Settings</Typography>
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes.advancedDetails}>
            <LambdaFieldsEditor
              value={lambda}
              onChange={handleLambdaFieldsChange}
              isAuthorizer={false}
              showLambdaSource={false}
              showS3Key={false}
              showCapacityProvider
              showLambdaIntegrations
              anchorName={name}
              labels={{
                envVars: 'Environment Variables (optional)',
              }}
            />
          </AccordionDetails>
        </Accordion>

        {/* IAM Configuration Section */}
        <LambdaIamSection
          iamPolicyNames={lambda.iam_policy_names || []}
          customIamPolicyNames={lambda.iam_policy_names_custom || []}
          onIamPolicyNamesChange={policies => onUpdate(name, 'iam_policy_names', policies)}
          onCustomIamPolicyNamesChange={policies =>
            onUpdate(name, 'iam_policy_names_custom', policies)
          }
          description="Configure IAM policies and permissions for this lambda function"
        />

        {/* Additional Settings Section */}
        <LambdaAdditionalSettingsSection
          lambda={lambda}
          onLambdaTriggerPathChange={value => onUpdate(name, 'lambda_trigger_path', value)}
          onDeadLetterQueueChange={value => onUpdate(name, 'dead_letter_queue', value)}
          onUseReadonlyProxyChange={value => onUpdate(name, 'use_readonly_proxy', value)}
        />
      </div>
    );
  },
);

LambdaItemEditor.displayName = 'LambdaItemEditor';
